import React from 'react'
import { observer } from 'mobx-react'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import { Form, FormGroupContainer, FormRow } from 'components/inputs'
import {
  CustomSelect,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from 'components/ModalSheet'
import AnimatedIcon from 'components/AnimatedIcon'
import { JoinWorkspaceAsAdminViewModel } from './JoinWorkspaceAsAdminViewModel'
import styles from './JoinWorkspaceAsAdminDialog.m.sass'

interface JoinWorkspaceAsAdminDialogProps {
  vm: JoinWorkspaceAsAdminViewModel
}

export const JoinWorkspaceAsAdminDialog = observer(
  ({ vm }: JoinWorkspaceAsAdminDialogProps) => {
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource: vm.searchWorkspaces,
      debounce: 300,
    })

    const error = vm.validation.getError('workspace')

    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader withCloseButton />

        <ModalSheetContent>
          <ModalSheetSection horizontallySpaced>
            <ModalSheetContentTitle>
              <span className={styles.titleRow}>
                <AnimatedIcon name="stars" size={28} />
                Join Workspace
                <AnimatedIcon name="stars" size={28} />
              </span>
            </ModalSheetContentTitle>
            <ModalSheetContentDescription compact>
              Select a workspace to join as an admin.
            </ModalSheetContentDescription>
          </ModalSheetSection>

          <ModalSheetSection spaced>
            <Form id="joinWorkspaceAsAdmin" onSubmit={vm.submit}>
              <FormRow>
                <FormGroupContainer label="Workspace" error={error}>
                  <CustomSelect
                    {...selectProps}
                    status={error ? 'danger' : 'default'}
                    placeholder="Select workspace"
                    itemToString={(v) => v?.name ?? ''}
                    value={vm.workspace ?? undefined}
                    onChange={(v) => vm.setWorkspace(v ?? null)}
                    multi={false}
                    disabled={vm.submit.pending}
                    data-test="join-workspace-as-admin-workspace-selector"
                    renderItem={(itemProps) => (
                      <SingleItemSelectedDefault
                        {...itemProps}
                        visual={
                          <Avatar
                            size={24}
                            subject={{
                              avatar: itemProps.item.branding.logos.icon,
                            }}
                          />
                        }
                      />
                    )}
                    renderSearchItem={({ item, ...itemProps }) => (
                      <SearchResultItem
                        {...itemProps}
                        visual={
                          <Avatar
                            size={24}
                            subject={{
                              avatar: item.branding.logos.icon,
                            }}
                          />
                        }
                      >
                        {item.name}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Cancel
            </Button>
            <Button
              submit
              form="joinWorkspaceAsAdmin"
              type="primary"
              disabled={vm.submit.pending}
              dataTest="join-workspace-as-admin-submit"
            >
              {vm.submit.pending ? 'Joining...' : 'Join Workspace'}
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)
